<template>
    <bible-cloud-dropdown
        @dropdownShown="dropdownShown"
        @dropdownHidden="dropdownHidden"
        :buttonText="currentBook ? currentBook.name : defaultBookName"
        :disabled="disabled"
        placeholderText="Book"
    >
        <div class="bibleCloudBookSelector-menu">
            <bible-cloud-search-input
                @inputUpdated="searchText = $event"
                ref="searchInput"
            ></bible-cloud-search-input>

            <div class="row pt-4">
                <div class="col-6 pr-0">
                    <div
                        class="bibleCloudDropdown-section-title"
                        :class="{'bibleCloudBookSelector-section-title-disabled': !oldTestamentBooks.length}"
                    >
                        old testament
                    </div>

                    <template  v-for="oldTestamentBook in oldTestamentBooks">
                        <router-link
                            class="bibleCloudBookSelector-book text-truncate"
                            :to="getBookUrl(oldTestamentBook)"
                        >
                            {{oldTestamentBook.name}}
                        </router-link>
                    </template>
                </div>

                <div class="col-6 pl-0">
                    <div
                        class="bibleCloudDropdown-section-title"
                        :class="{'bibleCloudBookSelector-section-title-disabled': !newTestamentBooks.length}"
                    >
                        new testament
                    </div>

                    <template v-for="newTestamentBook in newTestamentBooks">
                        <router-link
                            class="bibleCloudBookSelector-book text-truncate"
                            :to="getBookUrl(newTestamentBook)"
                        >
                            {{newTestamentBook.name}}
                        </router-link>
                    </template>
                </div>
            </div>
        </div>
    </bible-cloud-dropdown>
</template>

<script>
    import BibleCloudDropdown from '@app/BibleCloudDropdown';
    import BibleCloudSearchInput from '@app/BibleCloudSearchInput';

    export default {
        components: {
            BibleCloudDropdown,
            BibleCloudSearchInput
        },
        props: {
            disabled: Boolean
        },
        data() {
            return {
                fullNewTestamentBooks: [],
                fullOldTestamentBooks: [],
                newTestamentBooks: [],
                oldTestamentBooks: [],
                searchText: ''
            };
        },
        created() {
            // Generate the dropdown content when the page 1st loads
            this.buildBookLists();
        },
        computed: {
            /**
             * Store the state's books array for easy referencing
             *
             * @return {Array}
             */
            books() {
                return this.$store.state.books;
            },

            /**
             * Store the state's currentBook object for easy referencing
             *
             * @return {Object}
             */
            currentBook() {
                return this.$store.getters.currentBook;
            },

            /**
             * Get the default book name from store state
             * Falls back to first book in the list if default doesn't exist
             *
             * @return {String}
             */
            defaultBookName() {                
                var defaultName = this.$store.state.defaultBookName || 'genesis';

                // If books are loaded, verify the default book exists
                if (this.books && this.books.length) {
                    var defaultBook = this.books.find(function(book) {
                        return book.name && book.name.toLowerCase() === defaultName.toLowerCase();
                    });

                    // If default book doesn't exist, use the first book
                    if (!defaultBook) {
                        return this.books[0].name;
                    }
                }

                return defaultName;
            }
        },

        watch: {
            books: function() {
                this.buildBookLists();

                // Only select default book if currentBook is not set
                if (!this.currentBook) {
                    this.ensureDefaultBookSelected();
                }
            },

            searchText: function() {
                // If there is an autocomplete value, only show books that match
                var search = this.searchText.toLowerCase();

                if (this.searchText.length) {
                    this.oldTestamentBooks = this.fullOldTestamentBooks.filter(function(book) {
                        return book.name.toLowerCase().indexOf(search) !== -1;
                    });

                    this.newTestamentBooks = this.fullNewTestamentBooks.filter(function(book) {
                        return book.name.toLowerCase().indexOf(search) !== -1;
                    });
                } else {
                    // Return old & new testaments back to the full list
                    this.oldTestamentBooks = this.fullOldTestamentBooks;
                    this.newTestamentBooks = this.fullNewTestamentBooks;
                }
            }
        },

        mounted: function() {
            // Ensure a default book is selected only if currentBook is not availableq
            if (!this.currentBook) {
                this.ensureDefaultBookSelected();
            }
        },

        methods: {
            buildBookLists() {
                // Clear out any previously recorded books so we don't get duplicates when the translation changes
                this.fullOldTestamentBooks = [];
                this.fullNewTestamentBooks = [];

                // Save each book in its appropriate testament so we can display them as separate lists
                this.books.forEach(function(book) {
                    if (book.name && book.name.toLowerCase() === 'matthew' || this.fullNewTestamentBooks.length) {
                        this.fullNewTestamentBooks.push(book);
                    } else {
                        this.fullOldTestamentBooks.push(book);
                    }
                }.bind(this));

                // Update the lists that we're displaying with all the books
                this.oldTestamentBooks = this.fullOldTestamentBooks;
                this.newTestamentBooks = this.fullNewTestamentBooks;
            },

            ensureDefaultBookSelected: function() {
                // Get the currently selected book from store (based on URL or state)
                var currentBook = this.currentBook;

                /**
                 * STEP 1: Check if a valid book is already selected
                 *
                 * - If user is already on a valid URL like /asv/exodus/1/16
                 * - Then currentBook will already be set (e.g., Exodus)
                 * - In that case, we should NOT override it
                 *
                 * Without this check:
                 * - It would always redirect to default book (Genesis)
                 */
                if (currentBook && currentBook.id) {
                    return;
                }

                /**
                 * STEP 2: Get default book name from computed property
                 *
                 * This usually comes from:
                 * - Vuex store (this.$store.state.defaultBookName)
                 * - Or fallback value like 'genesis'
                 */
                var defaultName = this.defaultBookName;

                /**
                 * STEP 3: Find the default book object from books list
                 *
                 * - defaultBookName is just a string (e.g., "genesis")
                 * - But we need full book object to navigate (with chapters, id, etc.)
                 *
                 * Safety checks:
                 * - Ensure b.name exists before calling toLowerCase()
                 * - Ensure defaultBookName is a string
                 */
                var book = this.books.find(function(b) {
                    return b.name && b.name.toLowerCase() === defaultName.toLowerCase();
                });

                /**
                 * STEP 4: Fallback if default book not found
                 *
                 * - Sometimes defaultBookName may not exist in current translation
                 *   (e.g., different naming like "Gen." vs "Genesis")
                 *
                 * In that case:
                 * - Use the first available book in the list
                 */
                if (!book && this.books && this.books.length) {
                    book = this.books[0];
                }

                /**
                 * STEP 5: Final validation before redirect
                 *
                 * Check:
                 * - book exists
                 * - book has chapters
                 * - chapters array is not empty
                 *
                 * - To prevent navigation to invalid or incomplete data
                 */
                if (book && book.chapters && book.chapters.length) {
                    this.$router.push(this.getBookUrl(book));
                }
            },

            /**
             * Update the url when a book has been selected from the list
             *
             * @param {Object} book
             */
            getBookUrl(book) {
                const firstBook = book.chapters[0];

                return this.$store.getters.getChapterUrl(
                    this.$store.state.currentBibleAbbreviation,
                    firstBook.id
                );
            },

            /**
             * The dropdown menu has finished opening
             */
            dropdownShown() {
                // Select the search input so users can start typing to search through books right away
                this.$refs.searchInput.select();
            },

            /**
             * The dropdown menu has finished closing
             */
            dropdownHidden() {
                // Clear out the search text input
                this.$refs.searchInput.clear();
            }
        }
    };
</script>

<style lang="scss" scoped>
    @import '~sass/variables';

    // Customize the spacing of the dropdown menu
    .bibleCloudBookSelector-menu {
        margin: 25px 25px 0 25px;
    }

    // Gray out the title if the search results remove all books from the list under it
    .bibleCloudBookSelector-section-title-disabled {
        opacity: 0.4;
    }

    // Each book in the list of book names
    .bibleCloudBookSelector-book {
        color: $textColor;
        display: block;
        margin: 8px 0;
        padding: 0 11px;
        text-decoration: none;

        &:hover {
            background: $veryLightGray;
        }
    }
</style>
